// ═══════════════════════════════════════════════════════════════
// MEMBER SPONSORSHIPS SECTION
// Path: src/components/shows/MemberSponsorshipsSection.jsx
//
// Shows all sponsorships for a member with fulfillment status.
// Lazy-loads on first render. Used in DonorDetailMain (2-col)
// and DonorDetailClassic.
//
// Props:
//   member       — member object (must have .id)
//   palette      — theme palette
//   canEdit      — Admin/Manager
//   canViewGifts — show dollar amounts
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 7
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { Film, Package } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, CHAPTER_ID } from "../../constants/appConfig";
import { SponsorshipCard } from "./SponsorshipCard";
import { sponsorshipsService } from "../../services/showSponsorshipsService";

export const MemberSponsorshipsSection = ({
  member,
  palette,
  canEdit = false,
  canViewGifts = false,
}) => {
  const { isAdmin, userProfile, showMessage } = useApp();
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadSponsorships = useCallback(async () => {
    if (!member?.id) return;
    try {
      setLoading(true);
      const data = await sponsorshipsService.getSponsorshipsByMember(member.id);
      setSponsorships(data);
    } catch (error) {
      showMessage("Failed to load sponsorships", "error");
    } finally {
      setLoading(false);
    }
  }, [member?.id, showMessage]);

  useEffect(() => {
    loadSponsorships();
  }, [loadSponsorships]);

  const handleDelete = async (sponsorshipId) => {
    try {
      await sponsorshipsService.deleteSponsorship(sponsorshipId);
      showMessage("Sponsorship deleted", "warning");
      await loadSponsorships();
    } catch (error) {
      showMessage("Failed to delete sponsorship", "error");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 20,
          color: palette.text3,
          fontSize: FONT_SIZES.xs,
          fontFamily: FONT_FAMILY,
        }}
      >
        Loading sponsorships...
      </div>
    );
  }

  if (sponsorships.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 20,
          color: palette.text3,
          fontSize: FONT_SIZES.xs,
          fontFamily: FONT_FAMILY,
        }}
      >
        No show sponsorships on record.
      </div>
    );
  }

  return (
    <div>
      {sponsorships.map((sponsorship) => (
        <SponsorshipCard
          key={sponsorship.id}
          sponsorship={sponsorship}
          canEdit={canEdit}
          canDelete={isAdmin}
          canViewGifts={canViewGifts}
          chapterId={CHAPTER_ID}
          userId={userProfile?.id}
          onUpdate={loadSponsorships}
          onDelete={handleDelete}
          showMessage={showMessage}
        />
      ))}
      <div
        style={{
          textAlign: "center",
          fontSize: FONT_SIZES.xs,
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          marginTop: 8,
        }}
      >
        {sponsorships.length} sponsorship{sponsorships.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};
